import { get, post, del } from './http';

export interface PointsLevelSetting {
  name: string;
  title: string;
  minPoints: number;
  color: string;
  icon: string;
}

export interface SystemSettings {
  contactEmail: string;
  contactNotifyEmail: string;
  icp: string;
  socialGithub: string;
  socialWeibo: string;
  socialWechat: string;
  socialTwitter: string;
  allowRegister: boolean;
  requireApprove: boolean;
  vipPrice: string;
  vipDuration: string;
  inviteOnly: boolean;
  loginAttempts: string;
  sessionTimeout: string;
  pointsRewardRegister: string;
  pointsRewardInviteInviter: string;
  pointsRewardInviteInvitee: string;
  pointsRewardLogin: string;
  pointsRewardLike: string;
  pointsRewardShare: string;
  pointsDailyLimitLike: string;
  pointsDailyLimitShare: string;
  pointsRewardCheckinBase: string;
  pointsRewardCheckin3Days: string;
  pointsRewardCheckin7Days: string;
  pointsRewardCheckin14Days: string;
  pointsRewardCheckin30Days: string;
  pointsLevels: PointsLevelSetting[];
}

export interface FeatureSwitches {
  invite: boolean;
  like: boolean;
  share: boolean;
  checkin: boolean;
}

export interface FetchSettingsResponse {
  settings: SystemSettings;
  features: FeatureSwitches;
}

const SYSTEM_SETTINGS_CACHE_TTL = 5000;
let systemSettingsInFlight: Promise<FetchSettingsResponse> | null = null;
let systemSettingsCachedAt = 0;
let systemSettingsCache: FetchSettingsResponse | null = null;

export function invalidateSystemSettingsCache() {
  systemSettingsInFlight = null;
  systemSettingsCachedAt = 0;
  systemSettingsCache = null;
}

/**
 * 1. 获取系统配置与功能开关
 */
export function getSystemSettings() {
  const now = Date.now();
  if (systemSettingsCache && now - systemSettingsCachedAt < SYSTEM_SETTINGS_CACHE_TTL) {
    return Promise.resolve(systemSettingsCache);
  }
  if (systemSettingsInFlight) {
    return systemSettingsInFlight;
  }

  systemSettingsInFlight = get<FetchSettingsResponse>('/admin/settings')
    .then((res) => {
      systemSettingsCache = res;
      systemSettingsCachedAt = Date.now();
      return res;
    })
    .finally(() => {
      systemSettingsInFlight = null;
    });

  return systemSettingsInFlight;
}

/**
 * 2. 更新系统配置与功能开关
 */
export function updateSystemSettings(payload: {
  settings?: Partial<SystemSettings>;
  features?: Partial<FeatureSwitches>;
}) {
  return post<{ message: string }>('/admin/settings', payload).then((res) => {
    invalidateSystemSettingsCache();
    return res;
  });
}

/**
 * 3. 获取数据备份历史
 */
export function getBackupHistory() {
  return get<any[]>('/admin/backup/history');
}

/**
 * 4. 执行一键导出 SQL 备份
 */
export function exportBackup() {
  return post<{ history: any[]; sqlContent: string; filename: string }>('/admin/backup/export');
}

/**
 * 5. 执行一键导入 SQL 还原
 */
export function importBackup(sqlContent: string) {
  return post<{ message: string }>('/admin/backup/import', { sqlContent });
}

/**
 * 6. 删除备份记录及物理文件
 */
export function deleteBackup(id: string) {
  return del<{ message: string; data?: any[] }>(`/admin/backup/${id}`);
}

export interface PublicSettingsResponse {
  allowRegister: boolean;
  inviteOnly: boolean;
  contactEmail: string;
  contactNotifyEmail?: string;
  icp: string;
  socialGithub: string;
  socialWeibo: string;
  socialWechat: string;
  socialTwitter: string;
  pointsLevels: PointsLevelSetting[];
  siteName?: string;
  siteSlogan?: string;
  logoPreview?: string;
  footerCopyright?: string;
  footerDesc?: string;
  pointsRewardLike?: string;
  pointsRewardShare?: string;
  pointsPolicy?: {
    registerRewardPoints: number;
    inviteInviterRewardPoints: number;
    inviteInviteeRewardPoints: number;
    loginRewardPoints: number;
    likeRewardPoints: number;
    shareRewardPoints: number;
    likeDailyLimit: number;
    shareDailyLimit: number;
    checkinRewardBase: number;
    checkinReward3Days: number;
    checkinReward7Days: number;
    checkinReward14Days: number;
    checkinReward30Days: number;
  };
  pointsRules?: Array<{
    key: string;
    label: string;
    rewardText: string;
    dailyLimitText: string;
    enabled: boolean;
  }>;
  features: {
    invite: boolean;
    like: boolean;
    share: boolean;
    checkin: boolean;
  };
}

const PUBLIC_SETTINGS_CACHE_TTL = 5000;
let publicSettingsInFlight: Promise<PublicSettingsResponse> | null = null;
let publicSettingsCachedAt = 0;
let publicSettingsCache: PublicSettingsResponse | null = null;

/**
 * 获取公开系统配置与功能开关 (供访客和登录用户直接访问，免鉴定)
 */
export function getPublicSettings() {
  const now = Date.now();
  if (publicSettingsCache && now - publicSettingsCachedAt < PUBLIC_SETTINGS_CACHE_TTL) {
    return Promise.resolve(publicSettingsCache);
  }
  if (publicSettingsInFlight) {
    return publicSettingsInFlight;
  }

  publicSettingsInFlight = get<PublicSettingsResponse>('/public/settings')
    .then((res) => {
      publicSettingsCache = res;
      publicSettingsCachedAt = Date.now();
      return res;
    })
    .finally(() => {
      publicSettingsInFlight = null;
    });

  return publicSettingsInFlight;
}

export default {
  getSystemSettings,
  updateSystemSettings,
  getBackupHistory,
  exportBackup,
  importBackup,
  deleteBackup,
  getPublicSettings
};
